// 3rd Party Libraries
import {featureFilter} from '@maplibre/maplibre-gl-style-spec';

// Libraries
import {normalize} from '@/lib/strings';
import {wrapError} from '@/lib/errors';

// Services and Classes
import {pSBC, toRGBA} from '@/services/GraphicService/colorUtilities';
import {getPaintColor} from './paintValues';
import type {MVTFeature} from '@/services/MapService/MVTLayers/types';
import {LegendGroup} from '@/lib/classes/Legend/LegendGroup';
import {LegendItem} from '@/lib/classes/Legend/LegendItem';

// Types
import type {StyleOptionsGroups} from './createLegendGroups.d';
import {MapLayer} from '../MapLayer';
import type {ExpressionSpecification, LayerSpecification} from '@maplibre/maplibre-gl-style-spec';

const baseStyles: StyleOptionsGroups = {
  1: {
    fillStyle: 'rgba(49,79,79,1)',
    radius: 5,
    selected: {
      fillStyle: 'rgba(255,255,0,0.5)',
      radius: 6,
    },
  },
  2: {
    strokeStyle: 'rgba(136, 86, 167, 1)',
    lineWidth: 3,
    selected: {
      strokeStyle: 'rgba(255,25,0,0.5)',
      lineWidth: 4,
    },
  },
  3: {
    fillStyle: 'rgba(188, 189, 220, 0.5)',
    strokeStyle: 'rgba(136, 86, 167, 1)',
    lineWidth: 1,
    selected: {
      fillStyle: 'rgba(255,140,0,0.3)',
      strokeStyle: 'rgba(255,140,0,1)',
      lineWidth: 2,
    },
  },
};

function parseLayerStyles(layer: LayerSpecification) {
  const parsedLayerStyles = structuredClone(baseStyles);
  // Check the layer type to know what paint values to retrieve
  if (layer.type === 'circle') {
    // Point - circle-color is the property on mapbox layers
    const circleColor = toRGBA(getPaintColor(layer, 'circle-color'));
    const strokeStyle = toRGBA(getPaintColor(layer, 'circle-stroke-color')) ?? circleColor;
    parsedLayerStyles[1].fillStyle = circleColor;
    parsedLayerStyles[1].strokeStyle = strokeStyle;
    parsedLayerStyles[1].selected.fillStyle = pSBC(0.5, circleColor);
    parsedLayerStyles[1].selected.strokeStyle = pSBC(0.5, circleColor);
  } else if (layer.type === 'line') {
    // LineString - line-color is the property on mapbox layers
    const lineColor = toRGBA(getPaintColor(layer, 'line-color'));
    parsedLayerStyles[2].strokeStyle = lineColor;
    parsedLayerStyles[2].selected.strokeStyle = pSBC(0.5, lineColor);
  } else if (layer.type === 'fill') {
    // Polygon - fill-color and fill-outline-color are the properties on mapbox layers
    const fillColor = toRGBA(getPaintColor(layer, 'fill-color'));
    const outlineColor = toRGBA(getPaintColor(layer, 'fill-outline-color'));
    parsedLayerStyles[3].fillStyle = fillColor;
    parsedLayerStyles[3].strokeStyle = outlineColor;
    parsedLayerStyles[3].selected.fillStyle = pSBC(0.5, fillColor);
    parsedLayerStyles[3].selected.strokeStyle = pSBC(0.5, outlineColor);
  }
  return parsedLayerStyles;
}

/** One legend entry, and the style layer(s) that describe it. */
interface LegendEntry {
  /** Style layer id the legend group, item label, and icon mapping are derived from. */
  id: string;
  layers: LayerSpecification[];
}

/** A polygon fill and its outline are the two types Esri can publish as a pair describing one symbol. */
const isFillOrLine = (layer: LayerSpecification) => layer.type === 'fill' || layer.type === 'line';

/**
 * Identifies which features a style layer draws: its source-layer plus its filter. Two style layers with the
 * same key draw the same features, so they describe one legend entry.
 *
 * Returns null when either is missing. A layer with no filter draws every feature in its source-layer, so
 * pairing on it would merge unrelated symbols together.
 */
const featureTargetKey = (layer: LayerSpecification): string | null => {
  const sourceLayer = layer['source-layer'];
  if (!sourceLayer || layer.filter == null) return null;
  return JSON.stringify([sourceLayer, layer.filter]);
};

/** The features an entry draws, or null unless every layer in it draws the same ones. */
const sharedTargetKey = (entry: LegendEntry): string | null => {
  const [first, ...rest] = entry.layers.map(featureTargetKey);
  return first && rest.every((key) => key === first) ? first : null;
};

/** Removes a trailing index segment, e.g. `Market Newmark/Rank 1/0` -> `Market Newmark/Rank 1`. */
const stripIndexSuffix = (id: string) => id.replace(/\/\d+$/, '');

/**
 * Returns the id shared by a pair of style layers: the id itself when they match, the id without its
 * trailing index when they differ only by that index, or null when the ids are unrelated.
 */
const pairedEntryId = (a: string, b: string): string | null => {
  if (a === b) return a;
  const stripped = stripIndexSuffix(a);
  // The stripped id has to keep a group segment, otherwise a class whose label is itself a number would
  // collapse to the group name.
  if (!stripped.includes('/') || stripped !== stripIndexSuffix(b)) return null;
  return stripped;
};

/**
 * Groups a stylesheet's layers into legend entries.
 *
 * Layers sharing an id are one entry. On top of that, a `fill` and a `line` layer drawing the same features
 * merge into one entry, because Esri publishes a polygon whose outline is styled separately (e.g. by Market
 * Momentum Index) as such a pair. The pair may share one id or be suffixed with `/0` and `/1`, so pairing is
 * driven by the features a layer draws rather than by its id.
 */
const groupStyleLayers = (styleLayers: LayerSpecification[]): LegendEntry[] => {
  const entriesById = new Map<string, LegendEntry>();
  for (const layer of styleLayers) {
    const entry = entriesById.get(layer.id);
    if (entry) {
      entry.layers.push(layer);
    } else {
      entriesById.set(layer.id, {id: layer.id, layers: [layer]});
    }
  }

  const entries: LegendEntry[] = [];
  const pairCandidates = new Map<string, LegendEntry>();
  for (const entry of entriesById.values()) {
    const pairable = entry.layers.every(isFillOrLine);
    const target = pairable ? sharedTargetKey(entry) : null;
    const candidate = target ? pairCandidates.get(target) : undefined;
    // Entries sharing a geometry type are separate symbols drawn on the same features, not a pair.
    const typesOverlap = candidate?.layers.some((l) => entry.layers.some((e) => e.type === l.type));
    // Unrelated ids are two symbols that happen to draw the same features, so they stay separate entries.
    const mergedId = candidate && !typesOverlap ? pairedEntryId(candidate.id, entry.id) : null;
    if (mergedId) {
      candidate!.layers.push(...entry.layers);
      candidate!.id = mergedId;
      continue;
    }
    if (target) {
      pairCandidates.set(target, entry);
    }
    entries.push(entry);
  }
  return entries;
};

const getKeyCaseInsensitive = (obj: object, key: string) => {
  const lowerKey = key.toLowerCase();
  for (const k in obj) {
    if (k.toLowerCase() === lowerKey) {
      return k;
    }
  }
  return undefined;
};

/** Given a stylesheet, return an array of Legend Items identified within. */
async function createLegendGroups(layer: MapLayer): Promise<LegendGroup[]> {
  const stylesheet = layer.styleSheet;
  const subLayers = layer.options.flatMap((o) => o.subLayers);

  let image: HTMLImageElement | null = null;
  if (layer.urlMap['iconAtlas'] && layer.urlMap['iconMapping']) {
    image = new Image();
    image.src = layer.urlMap['iconAtlas'];
    await new Promise((resolve, reject) => {
      image!.onload = resolve;
      image!.onerror = (e) => {
        const wrappedError = wrapError(e, `Error loading icon atlas for layer "${layer.id}"`);
        console.error(wrappedError);
        reject(wrappedError);
      };
    });
  }
  // Collect unique groups from the layer ids
  const groupLabels = Array.from(
    new Set(
      stylesheet.layers
        .map((l) => l.id.split('/')[0])
        .filter(Boolean),
    ),
  );

  // Map group labels to LegendGroup instances
  const legendGroups: LegendGroup[] = groupLabels.map((label) =>
    new LegendGroup({label, legendSymbols: []}),
  );

  // Helper to get type number from layer type
  const typeNumMap = {circle: 1, line: 2, fill: 3, symbol: 4} as const;

  // Build a lookup for legendGroups by label
  const groupMap = Object.fromEntries(legendGroups.map((g) => [g.label, g]));

  // Group stylesheet layers into legend entries. In some cases, one legend item is represented by multiple
  // style layers, e.g. a polygon whose fill and outline are published as separate 'fill' and 'line' layers.
  const legendEntries = groupStyleLayers(stylesheet.layers);

  // Iterate over each legend entry
  for (const {id: layerId, layers} of legendEntries) {
    const group = groupMap[layerId.split('/')[0]];
    if (!group) continue;

    // The first style layer defines the entry, except for a fill+line pair, where the fill defines the
    // swatch and the line contributes its outline. Selecting by type means stylesheet order does not matter.
    const fillLayer = layers.find((l) => l.type === 'fill');
    const lineLayer = layers.find((l) => l.type === 'line');
    const isFillLinePair = Boolean(fillLayer && lineLayer);
    const primaryLayer = isFillLinePair ? fillLayer! : layers[0];

    const typeNum = typeNumMap[primaryLayer.type as keyof typeof typeNumMap];
    if (!typeNum) continue;

    const styleOptions = parseLayerStyles(primaryLayer);
    const opts = styleOptions[typeNum] || {};
    // A paired line layer carries the feature's own outline color, so it takes precedence over the fill
    // layer's static fill-outline-color, matching how the map draws it (see paintFeature in featurePaint).
    const pairedStroke = isFillLinePair ? toRGBA(getPaintColor(lineLayer, 'line-color')) : null;
    const fillColor = opts.fillStyle ?? opts.strokeStyle ?? null;
    const strokeColor = pairedStroke ?? opts.strokeStyle ?? null;

    // Extract label from layer.id
    let label = layerId.slice(layerId.indexOf('/') + 1);
    if ((layerId.match(/\//g) || []).length === 2) {
      label = layerId.split('/')[2];
    }

    let visible = true;
    let filterKeys = null;
    let legendToggleEnabled = true;
    for (const sub of subLayers) {
      legendToggleEnabled = sub.legendToggleEnabled ?? true;
      for (const g of sub.legendGroups) {
        if (g.label !== group.label) continue;
        group.legendToggleEnabled = legendToggleEnabled;
        filterKeys = sub.filterKeys;
        // Check if the item visibility exists in the user layer settings
        const symbol = g.legendSymbols?.find((ls) => normalize(ls.label) === normalize(label));
        if (symbol) {
          visible = symbol.visible;
        }
      }
    }
    let legendImage: HTMLImageElement | null = null;
    let imageOpts;
    if (image) {
      const mapping = await fetch(layer.urlMap['iconMapping']);
      const mappingJson = await mapping.json();
      // Similarly to ESRI stylesheets, icon mapping options are separated by layer ID.
      const options = mappingJson[layerId];
      if (options) {
        // Share the atlas image for all legend items.
        legendImage = image;
        imageOpts = {
          // dimensions of the particular icon in the atlas
          width: options.width,
          height: options.height,
          // where that icon is located in the atlas
          offsetX: options.x || 0,
          offsetY: options.y || 0,
        };
      }
    }

    // `featureFilter` compiles an expression tree on every call, so it is hoisted out of the matcher below,
    // which runs once per feature per redraw. A style layer carrying its own filter compiles once, here.
    // A layer without one builds its filter around a property key read off the feature, so those compile
    // on first sight of each key and are cached by it.
    let aFilterFailedToCompile = false;
    const layerMatchers = layers.map((styleLayer) => {
      const sourceLayer = styleLayer['source-layer'];
      if (!styleLayer.filter) return {sourceLayer, staticMatch: null};
      try {
        return {
          sourceLayer,
          staticMatch: featureFilter(['all', styleLayer.filter] as ExpressionSpecification).filter,
        };
      } catch (e) {
        console.error(wrapError(e, `Error compiling legend filter for layer "${layerId}"`));
        aFilterFailedToCompile = true;
        return {sourceLayer, staticMatch: null};
      }
    });

    const matchByFilterKey = new Map<string, ReturnType<typeof featureFilter>['filter']>();
    const getMatchForKey = (filterKey: string) => {
      let match = matchByFilterKey.get(filterKey);
      if (!match) {
        match = featureFilter(['all', ['==', filterKey, label]] as ExpressionSpecification).filter;
        matchByFilterKey.set(filterKey, match);
      }
      return match;
    };

    group.legendSymbols.push(new LegendItem({
      label,
      fillColor,
      strokeColor,
      visible,
      image: legendImage,
      imageOpts,
      matcherFn: (feature: MVTFeature) => {
        if (!label) return true;
        if (!legendToggleEnabled) return true; // If legend toggle is disabled, always return true
        if (aFilterFailedToCompile) return true;
        try {
          // The feature matches the legend item if it satisfies any one of the entry's style layers.
          for (const {sourceLayer, staticMatch} of layerMatchers) {
            let match = staticMatch;
            if (!match) {
              let filterKey: string | undefined;
              if (filterKeys?.[label]) {
                filterKey = getKeyCaseInsensitive(feature.properties, filterKeys[label]);
              } else {
                filterKey = getKeyCaseInsensitive(feature.properties, 'layerName') ||
                  getKeyCaseInsensitive(feature.properties, 'layerType');
              }
              if (!filterKey) return true;
              match = getMatchForKey(filterKey);
            }

            // NOTE: Matching the 'source-layer' is not included in the MapBox filter because the expression syntax
            // must match for all of the expressions in the array, and we would need to update all of the ESRI
            // stylesheets to use the updated syntax for the filter function. Some layers, like the OSM layers,
            // use the new syntax.
            // old syntax: ['==', 'layerName', layer['source-layer']]
            // new syntax: ['==', ['get', 'source-layer'], layer['source-layer']]
            // see: https://github.com/mapbox/mapbox-gl-js/issues/6656
            if (match({zoom: 0}, feature) && feature.properties?.layerName === sourceLayer) return true;
          }
          return false;
        } catch (e) {
          const wrappedError = wrapError(e, `Error creating legend item for layer "${layerId}"`);
          console.error(wrappedError);
          return true; // Fallback to true so that the layer still displays
        }
      },
    }));
  }

  return legendGroups;
}

export {
  createLegendGroups,
};
